/**
 * The per-day status pill for the family weekly view, in the design system's
 * "Light" badge variation (tinted background + colored text + matching border).
 * Color comes from the semantic tone:
 *   - info (blue)    → "Order by …" while the window is open
 *   - success (green)→ "Edit by …" / "Complete" (ordered)
 *   - warning (amber)→ the deadline is today (order/edit urgently)
 *   - neutral (gray) → "Ordering closed" (window passed, nothing ordered)
 *
 * The info blue is deliberately distinct from the primary "Review & Checkout"
 * button so the deadline pill doesn't read as a CTA.
 */

import { cn } from '@ordo/ui/cn'
import type { ReactElement } from 'react'

import type { DayBadgeTone } from '@/lib/family-orders/day-status'

const TONE_STYLES: Record<DayBadgeTone, string> = {
  info: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900',
  success:
    'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
  neutral: 'bg-zinc-100 text-zinc-600 ring-zinc-200 dark:bg-zinc-800/60 dark:text-zinc-300 dark:ring-zinc-700',
}

export function DayStatusBadge({ tone, label }: { tone: DayBadgeTone; label: string }): ReactElement | null {
  if (!label) {
    return null
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset',
        TONE_STYLES[tone],
      )}>
      {label}
    </span>
  )
}
